import { FC, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { useReportsContext, useYupValidationResolver } from "@ryde/redux";
import {
  hasKeys,
  isEqualStringArraysAnyOrder,
  Log,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsVisibilityAF,
} from "@ryde/shared";

import {
  FlexGroupingFields,
  flexGroupingPanelValidationSchema,
  FlexGroupingPeriod,
  FlexGroupingWrapper,
  IFlexGroupingPanelForm,
} from "./parts";

export const FlexGroupingPanel: FC = () => {
  const { reportId, reportTemplate } = useReportsContext();
  const previousReportRef = useRef<number>(reportId);
  const isUpdatingGroupingFromFormRef = useRef(false);
  const setColumnVisibilityState = useSetRecoilState(
    reportsTableColumnsVisibilityAF(reportId),
  );
  const setColumnOrderState = useSetRecoilState(
    reportsTableColumnsOrderStateAF(reportId),
  );

  const [periodGrouping, setPeriodGrouping] = useRecoilState(
    reportGroupingPeriodAF(reportId),
  );
  const [groupingParams, setGroupingParams] = useRecoilState(
    reportFlexGroupingParamsAF(reportId),
  );

  const resolver = useYupValidationResolver(flexGroupingPanelValidationSchema);
  const methods = useForm<IFlexGroupingPanelForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: { periodGrouping, groupingParams },
    resolver,
  });
  const { watch, reset, clearErrors } = methods;
  useEffect(() => {
    if (
      previousReportRef.current !== reportId &&
      periodGrouping &&
      groupingParams
    ) {
      previousReportRef.current = reportId;
      clearErrors();
      reset({ periodGrouping, groupingParams });
    }
  }, [clearErrors, groupingParams, periodGrouping, reportId, reset]);

  const watchedPeriodGrouping = watch("periodGrouping");
  const watchedGroupingParams = watch("groupingParams");

  const resetPreviousLayout = useEvent(() => {
    Log.debug("Reset report layout on grouping parameters change");
    setColumnVisibilityState({});
    setColumnOrderState([]);
  });

  useEffect(() => {
    setPeriodGrouping(watchedPeriodGrouping);
    setGroupingParams(watchedGroupingParams);
    isUpdatingGroupingFromFormRef.current = true;
  }, [
    setGroupingParams,
    setPeriodGrouping,
    watchedGroupingParams,
    watchedPeriodGrouping,
  ]);

  useEffect(() => {
    if (isUpdatingGroupingFromFormRef.current) {
      isUpdatingGroupingFromFormRef.current = false;
      return;
    }

    const periodMismatch = watchedPeriodGrouping !== periodGrouping;
    const paramsMismatch = !isEqualStringArraysAnyOrder(
      watchedGroupingParams ?? [],
      groupingParams ?? [],
    );

    if (periodMismatch || paramsMismatch) {
      reset({
        periodGrouping,
        groupingParams: groupingParams ?? [],
      });
    }
  }, [
    groupingParams,
    periodGrouping,
    reset,
    watchedGroupingParams,
    watchedPeriodGrouping,
  ]);

  useEffect(() => {
    if (
      previousReportRef.current === reportId &&
      (watchedPeriodGrouping !== periodGrouping ||
        !isEqualStringArraysAnyOrder(watchedGroupingParams, groupingParams))
    ) {
      resetPreviousLayout();
    }
  }, [
    groupingParams,
    periodGrouping,
    reportId,
    resetPreviousLayout,
    watchedGroupingParams,
    watchedPeriodGrouping,
  ]);

  return (
    <FormProvider {...methods}>
      <FlexGroupingWrapper>
        <div className="period-grouping">
          {reportTemplate?.periodGroupingColumn && <FlexGroupingPeriod />}
        </div>
        <div className="grouping-params">
          {hasKeys(reportTemplate?.flexGroupingColumns) && (
            <FlexGroupingFields />
          )}
        </div>
      </FlexGroupingWrapper>
    </FormProvider>
  );
};
